import type {
  OrderCreateAttachmentInput,
  OrderDetailDTO,
  OrderDTO,
  OrderExportLineDTO,
  OrderExportRequestInput,
  OrderSummaryDTO,
  OrderUpdateChannelInput,
  OrderUpdateCustomerInfoInput,
  OrderUpdateDiscountInput,
  OrderUpdateItemsInput,
  OrderUpdatePaymentMethodInput,
  OrderUpdateStatusInput,
  OrderUpdateTrackingInput,
} from "@/modules/orders/domain/contracts";
import type {
  OffsetPageDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type { OrderDetailDTO, OrderDTO, OrderSummaryDTO };

export type { OrderCreateAttachmentInput } from "@/modules/orders/domain/contracts";

export const ordersApi = {
  async list(query: OffsetPageQueryDTO): Promise<OffsetPageDTO<OrderDTO>> {
    const url = new URL(`${config.apiUrl}/orders`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<OffsetPageDTO<OrderDTO>>(url.toString());
  },

  async exportLines(
    body: OrderExportRequestInput,
  ): Promise<{ lines: OrderExportLineDTO[] }> {
    return fetcher.post<{ lines: OrderExportLineDTO[] }>(
      `${config.apiUrl}/orders/export`,
      body,
    );
  },

  async get(id: string): Promise<OrderDetailDTO> {
    return fetcher.get<OrderDetailDTO>(`${config.apiUrl}/orders/${id}`);
  },

  async getSummary(): Promise<OrderSummaryDTO> {
    return fetcher.get<OrderSummaryDTO>(`${config.apiUrl}/orders/summary`);
  },

  async updateStatus(
    id: string,
    input: OrderUpdateStatusInput,
  ): Promise<OrderDetailDTO> {
    return fetcher.patch<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/status`,
      input,
    );
  },

  async updateTracking(
    id: string,
    input: OrderUpdateTrackingInput,
  ): Promise<OrderDetailDTO> {
    return fetcher.patch<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/tracking`,
      input,
    );
  },

  async createHalPreOrder(id: string): Promise<OrderDetailDTO> {
    return fetcher.post<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/hal/pre-order`,
      {},
    );
  },

  async updateHalPreOrder(id: string): Promise<OrderDetailDTO> {
    return fetcher.patch<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/hal/pre-order`,
      {},
    );
  },

  async cancelHalPreOrder(id: string): Promise<OrderDetailDTO> {
    return fetcher.delete<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/hal/pre-order`,
    );
  },

  async refreshHalTracking(id: string): Promise<OrderDetailDTO> {
    return fetcher.post<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/hal/tracking`,
      {},
    );
  },

  async updateCustomerInfo(
    id: string,
    input: OrderUpdateCustomerInfoInput,
  ): Promise<OrderDetailDTO> {
    return fetcher.patch<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/customer-info`,
      input,
    );
  },

  async updateDiscount(
    id: string,
    input: OrderUpdateDiscountInput,
  ): Promise<OrderDetailDTO> {
    return fetcher.patch<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/discount`,
      input,
    );
  },

  async updateItems(
    id: string,
    input: OrderUpdateItemsInput,
  ): Promise<OrderDetailDTO> {
    return fetcher.patch<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/items`,
      input,
    );
  },

  async updatePaymentMethod(
    id: string,
    input: OrderUpdatePaymentMethodInput,
  ): Promise<OrderDetailDTO> {
    return fetcher.patch<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/payment-method`,
      input,
    );
  },

  async updateChannel(
    id: string,
    input: OrderUpdateChannelInput,
  ): Promise<OrderDetailDTO> {
    return fetcher.patch<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/channel`,
      input,
    );
  },

  async createAttachment(
    id: string,
    input: OrderCreateAttachmentInput,
  ): Promise<OrderDetailDTO> {
    return fetcher.post<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/attachments`,
      input,
    );
  },

  async deleteAttachment(
    id: string,
    attachmentId: string,
  ): Promise<OrderDetailDTO> {
    return fetcher.delete<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/attachments/${attachmentId}`,
    );
  },

  async createReturnLines(
    id: string,
    body: {
      lines: {
        orderItemId: string;
        quantityReturned: number;
        reason?: string | null;
      }[];
    },
  ): Promise<OrderDetailDTO> {
    return fetcher.post<OrderDetailDTO>(
      `${config.apiUrl}/orders/${id}/return-lines`,
      body,
    );
  },
};
